import { MultiAutoCompleteItem } from "@/components/MultiAutoComplete/MultiAutoComplete";
import { CompanyEnrichAutoCompleteItem } from "@/pages/Private/pages/CompanyEnrich/components/CompanyEnrichAutoComplete";
import {
	CreateCompanyEnrich,
	Industry,
} from "@/pages/Private/pages/CompanyEnrich/schema/companyEnrich";

import { IcpInfo } from "../../schema/icp";

/** The "find different people" pane's local state, one entry per control. */
export interface TrackFilterValues {
	positionQuery: CompanyEnrichAutoCompleteItem[];
	seniority: MultiAutoCompleteItem[];
	department: MultiAutoCompleteItem[];
	countries: CompanyEnrichAutoCompleteItem[];
	// Industries carry their NAICS codes — that is what the company search filters on.
	industries: Industry[];
	employees: MultiAutoCompleteItem[];
	companyCountries: CompanyEnrichAutoCompleteItem[];
	semanticQuery: string;
	skills: string[];
	/** Company keywords — what the employer does, not what the person can do. */
	keywords: string[];
}

export const EMPTY_TRACK_FILTERS: TrackFilterValues = {
	positionQuery: [],
	seniority: [],
	department: [],
	countries: [],
	industries: [],
	employees: [],
	companyCountries: [],
	semanticQuery: "",
	skills: [],
	keywords: [],
};

const ids = (items: { id: string | number }[]): string[] => items.map((item) => String(item.id));

/**
 * The pane's state as a `company-enrich/people/search/preview` body — the same
 * search the Lead DB form runs. Empty controls are omitted so the upstream sees
 * no filter for that dimension rather than an empty one.
 */
export const toPreviewRequest = (values: TrackFilterValues): CreateCompanyEnrich => {
	const skills = values.skills.join(", ").trim();
	const semanticQuery = values.semanticQuery.trim();
	const keywords = values.keywords.map((keyword) => keyword.trim()).filter(Boolean);

	const companyFilterSearch = {
		...(semanticQuery ? { semanticQuery } : {}),
		...(values.industries.length ? { industries: values.industries } : {}),
		...(values.employees.length ? { employees: ids(values.employees) } : {}),
		...(values.companyCountries.length ? { countries: ids(values.companyCountries) } : {}),
		...(keywords.length ? { keywords } : {}),
	};

	return {
		...(values.positionQuery.length ? { positionQuery: ids(values.positionQuery) } : {}),
		...(values.seniority.length ? { seniority: ids(values.seniority) } : {}),
		...(values.department.length ? { department: ids(values.department) } : {}),
		...(values.countries.length ? { countries: ids(values.countries) } : {}),
		...(skills ? { query: skills } : {}),
		...(Object.keys(companyFilterSearch).length ? { companyFilterSearch } : {}),
	} as CreateCompanyEnrich;
};

/** Whether the pane has anything to search with — gates the Search button. */
export const hasAnyTrackFilter = (values: TrackFilterValues): boolean =>
	Object.keys(toPreviewRequest(values)).length > 0;

/**
 * The pane's starting point: the confirmed ICP as a people search. The personas
 * become the job titles and the industries/keywords/location become a semantic
 * company query — which the user can then edit or clear, since an over-narrow
 * ICP is exactly what leaves the table empty.
 */
export const fromIcp = (
	icp: IcpInfo,
	countries: CompanyEnrichAutoCompleteItem[]
): TrackFilterValues => {
	const parts = [
		icp.industries.length ? `${icp.industries.join(", ")} companies` : "companies",
		icp.location ? `in ${icp.location}` : "",
		icp.keywords ? `focused on ${icp.keywords}` : "",
	].filter(Boolean);

	return {
		...EMPTY_TRACK_FILTERS,
		positionQuery: icp.personas.map((persona) => ({ id: persona, title: persona })),
		countries,
		companyCountries: countries,
		semanticQuery: parts.join(" "),
	};
};
